import { isAxiosError } from 'axios';
import api from './api';
import { cache } from '../utils/cache';
import { queryClient } from '../lib/queryClient';
import type { OnboardingState, OnboardingReport } from '../types/onboarding';

// Onboarding seed and cleanup create and delete sample rows across nearly every
// module — suppliers, inventory items and lots, customers, cuppers, machines,
// purchase orders, work orders, cupping sessions, sales orders, finished goods and
// everything the reports aggregate from them — directly on the backend, bypassing
// the per-entity services that normally keep the client caches honest.
//
// TWO independent caches hold that data, and only one of them was being cleared:
//   - the module-level CacheManager (5-min TTL) behind the picker services, and
//   - React Query, where the purchase-order, sales-order and work-order LIST pages
//     live with a 5-minute staleTime.
// React Query was never touched here at all, which is why the practice purchase
// order and sales order kept rendering on their list pages after the tour had
// deleted them server-side — the rows were gone from the API and still cached in
// the browser. The CacheManager half also enumerated its keys and missed
// sales_orders, work_orders and cupping_sessions.
//
// Clear both wholesale rather than naming keys. A seed/cleanup transition happens
// a handful of times in a user's life and moves data in nearly every module, so a
// hand-maintained key list is exactly the thing that goes stale — and both caches
// are read-through, so the cost of over-clearing is one refetch.
const invalidateSampleEntityCaches = () => {
  cache.clear();
  // No filter: mark every query stale and refetch the ones currently mounted, so
  // the list the user is looking at when the tour ends updates in place.
  void queryClient.invalidateQueries();
};

export const onboardingService = {
  getState: async (): Promise<OnboardingState> => (await api.get('/users/me/onboarding-state')).data,
  start: async (): Promise<OnboardingState> => (await api.post('/onboarding/start')).data,
  seed: async (): Promise<{ supplierId: string; greenItemId: string; customerId: string; cupperId: string; machineTypeId: string; roastedBulkId1?: string; roastedBulkId2?: string; finishedGoodId?: string }> => {
    const data = (await api.post('/onboarding/seed')).data;
    invalidateSampleEntityCaches();
    return data;
  },
  advance: async (completedStep?: number): Promise<OnboardingState> =>
    (await api.post('/onboarding/advance', completedStep != null ? { completed_step: completedStep } : {})).data,
  report: async (): Promise<OnboardingReport> => (await api.get('/onboarding/report')).data,
  skip: async (): Promise<OnboardingState> => {
    const data = (await api.post('/onboarding/skip')).data;
    invalidateSampleEntityCaches();
    return data;
  },
  replay: async (): Promise<OnboardingState> => {
    const data = (await api.post('/onboarding/replay')).data;
    invalidateSampleEntityCaches();
    return data;
  },
  complete: async (): Promise<{ state: OnboardingState; nextActions: string[] }> => {
    const data = (await api.post('/onboarding/complete')).data;
    invalidateSampleEntityCaches();
    return data;
  },

  // Practice-chain orchestration methods (option A: overlay orchestrates creation)

  // 1) Practice purchase order (received, so a green lot is created). Returns the new green lot id.
  createSamplePurchaseOrder: async (supplierId: string, greenItemId: string): Promise<{ poId: string; greenLotId: string }> => {
    const res = await api.post('/purchase-orders', {
      supplier_id: supplierId,
      status: 'ordered',
      payment_status: 'unpaid',
      delivery_status: 'received',
      items: [{ item_id: greenItemId, quantity_ordered: 10, quantity_received: 10, unit_price: 100 }],
      onboarding_sample: true,
    });
    const po = res.data;
    const greenLotId = po?.items?.[0]?.lot_id ?? '';
    return { poId: po.id, greenLotId };
  },

  // 2) Practice roast batch: create then complete. Returns the roasted output item id.
  createSampleWorkOrder: async (greenItemId: string, greenLotId: string, machineTypeId: string): Promise<{ woId: string; woNumber: string; roastedItemId: string }> => {
    const today = new Date().toISOString().slice(0, 10);
    const createRes = await api.post('/work-orders', {
      date: today,
      time: '08:00',
      machine_type_id: machineTypeId,
      roaster: 'Onboarding',
      roast_type: 'Filter',
      input_weight: 10,
      output_weight: 8,
      use_roast_profile: false,
      lots: [{ item_id: greenItemId, lot_id: greenLotId, quantity: 10 }],
      onboarding_sample: true,
    });
    const woId = createRes.data.id;
    const completeRes = await api.post(`/work-orders/${woId}/complete`, {});
    const wo = completeRes.data;
    return { woId, woNumber: wo.work_order_number ?? createRes.data.work_order_number ?? '', roastedItemId: wo.item_id ?? '' };
  },

  // Mirrors the two roasts SeedSampleRoastedBulks creates, in the same order, so
  // an auto-created session reads the same as one built through the picker.
  ONBOARDING_PRACTICE_ROASTS: [
    { itemName: 'Sample Roast \u2014 Filter (Onboarding)', roastType: 'Filter' },
    { itemName: 'Sample Roast \u2014 Espresso (Onboarding)', roastType: 'Espresso' },
  ] as const,

  // 3) Practice cupping session over the two seeded practice roasts.
  //
  // Cupping is a COMPARISON: the API rejects a session with fewer than two
  // roasted bulks. This used to post the single roast from step 3 and fail with
  // "Session must include at least 2 roasted bulk items" — invisible while Next
  // was disabled on guided steps, and the first thing a returning user hits now
  // that they can skip ahead. Both columns are FKs to inventory_items, so the
  // seeded item id serves as roasted_bulk_id and inventory_item_id alike.
  //
  // No work order is attributed: the seeded roasts were not produced by the
  // user's step-3 run, and both columns are nullable, so claiming otherwise
  // would put a false lineage on the practice session.
  createSampleCupping: async (roastedBulkIds: string[], cupperId: string): Promise<{ cuppingId: string }> => {
    const ids = roastedBulkIds.filter(Boolean);
    if (ids.length < 2) {
      throw new Error('The practice cupping compares two roasts, and only one is available.');
    }
    try {
      const res = await api.post('/cupping-sessions', {
        cupper_id: cupperId,
        blind_mode: false,
        cupping_mode: 'Simplified',
        details: ids.map((id, index) => ({
          roasted_bulk_id: id,
          inventory_item_id: id,
          item_name:
            onboardingService.ONBOARDING_PRACTICE_ROASTS[index]?.itemName ?? 'Sample Roast (Onboarding)',
          roast_type: onboardingService.ONBOARDING_PRACTICE_ROASTS[index]?.roastType ?? 'Filter',
          order_index: index,
        })),
        onboarding_sample: true,
      });
      return { cuppingId: res.data.id };
    } catch (e) {
      // A roasted bulk can only sit in one draft session at a time, so this 409s
      // when the user already started the practice cupping through the guide and
      // left it unscored. That draft IS the record this step is meant to produce
      // — adopt it rather than reporting a conflict the user can do nothing about.
      if (isAxiosError(e)) {
        const data = e.response?.data as
          | { error?: string; items?: Array<{ session_id?: string }> }
          | undefined;
        const existingId =
          data?.error === 'cupping_session.item_in_draft' ? data.items?.[0]?.session_id : undefined;
        if (existingId) return { cuppingId: existingId };
      }
      throw e;
    }
  },

  // 4) Persist the chained ids into onboarding_state.sampleRefs (server) for resume-after-reload.
  updateState: async (state: OnboardingState): Promise<OnboardingState> => (await api.put('/users/me/onboarding-state', state)).data,
};
